import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    DeskPanel,
    DeskEmpty,
    Pill,
    DESK_BUTTON_CLASS,
    TINY_BUTTON_CLASS,
    ASKFRANK_TINT,
} from './AskFrankDesk';
import { ENTITY_KINDS, kindLabel, splitCues } from './knowledgeShared';
import { promoteAskFrankCandidate, dismissAskFrankCandidate } from '../../../services/api';

const SWIPE_DISTANCE = 130;

function NameReview({ candidates, onClose }) {
    const [index, setIndex] = useState(0);
    const [kinds, setKinds] = useState([]);
    const [cues, setCues] = useState({});
    const [terms, setTerms] = useState([]);
    const [termDraft, setTermDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(0);
    const [flying, setFlying] = useState(null);

    const dragStart = useRef(null);
    const candidate = candidates[index] || null;

    useEffect(() => {
        setKinds(candidate?.kindGuess ? [candidate.kindGuess] : []);
        setCues({});
        setTerms([]);
        setTermDraft('');
        setDrag(0);
        setFlying(null);
    }, [candidate]);

    const advance = useCallback(() => {
        setIndex(current => current + 1);
    }, []);

    const dismiss = useCallback(async () => {
        if (!candidate || busy) return;

        setBusy(true);
        setFlying('left');
        const res = await dismissAskFrankCandidate(candidate.id);
        setBusy(false);

        if (!res.success) {
            setFlying(null);
            toast.error(res.message || 'That did not work');
            return;
        }

        advance();
    }, [candidate, busy, advance]);

    const file = useCallback(async () => {
        if (!candidate || busy) return;

        const chosen = kinds.length > 0 ? kinds : [candidate.kindGuess || 'other'];

        setBusy(true);
        setFlying('right');

        const res = await promoteAskFrankCandidate(candidate.id, {
            senses: chosen.map(kind => ({ kind, cues: splitCues(cues[kind]) })),
            aliases: terms,
            status: 'active',
        });

        setBusy(false);

        if (!res.success) {
            setFlying(null);
            toast.error(res.message || 'That did not work');
            return;
        }

        const names = chosen.map(kind => kindLabel(kind).toLowerCase()).join(' and ');
        toast.success(`${candidate.name} filed as ${names}`);
        advance();
    }, [candidate, busy, kinds, cues, terms, advance]);

    const toggleKind = useCallback((value) => {
        setKinds(current => (current.includes(value)
            ? current.filter(kind => kind !== value)
            : [...current, value]));
    }, []);

    useEffect(() => {
        const onKey = (event) => {
            if (event.target?.tagName === 'INPUT') return;

            if (event.key === 'ArrowLeft') { event.preventDefault(); dismiss(); }
            else if (event.key === 'ArrowRight') { event.preventDefault(); file(); }
            else if (/^[1-9]$/.test(event.key)) {
                const picked = ENTITY_KINDS[Number(event.key) - 1];
                if (picked) toggleKind(picked.value);
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dismiss, file, toggleKind]);

    const onPointerDown = (event) => {
        if (busy) return;
        dragStart.current = event.clientX;
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
        if (dragStart.current === null) return;
        setDrag(event.clientX - dragStart.current);
    };

    const onPointerUp = () => {
        if (dragStart.current === null) return;

        const moved = drag;
        dragStart.current = null;
        setDrag(0);

        if (moved > SWIPE_DISTANCE) file();
        else if (moved < -SWIPE_DISTANCE) dismiss();
    };

    const addTerm = () => {
        const cleaned = termDraft.trim();
        if (!cleaned) return;
        if (!terms.includes(cleaned)) setTerms([...terms, cleaned]);
        setTermDraft('');
    };

    if (!candidate) {
        return (
            <DeskPanel
                title="Review names"
                right={<button type="button" onClick={onClose} className="text-[11px] uppercase text-white/80 hover:text-white">Close</button>}
            >
                <DeskEmpty>
                    {candidates.length === 0
                        ? 'Nothing waiting. Run "Look for new names" in Lab to fill the pile.'
                        : 'That is the lot. Nice work.'}
                </DeskEmpty>
            </DeskPanel>
        );
    }

    const offset = flying === 'left' ? -600 : flying === 'right' ? 600 : drag;
    const tilt = offset / 22;
    const verdict = offset > 60 ? 'file' : offset < -60 ? 'drop' : null;
    const fileLabel = kinds.length === 0
        ? `File as ${kindLabel(candidate.kindGuess).toLowerCase()}`
        : `File as ${kinds.map(kind => kindLabel(kind).toLowerCase()).join(' + ')}`;

    return (
        <DeskPanel
            title="Review names"
            right={
                <div className="flex items-center gap-3">
                    <span className="text-[11px] uppercase text-white/70">{index + 1} of {candidates.length}</span>
                    <button type="button" onClick={onClose} className="text-[11px] uppercase text-white/80 hover:text-white">
                        Close
                    </button>
                </div>
            }
        >
            <p className="mb-3 text-[12px] text-gray-500">
                Names Frank keeps seeing in the pages he reads. Say what each one is and he files it as a live entry
                straight away, no writing needed. A name can be several things at once, so tick them all. Drag the card
                right to file it, left to drop it, or use the arrow keys. Number keys tick a kind.
            </p>

            <div className="flex flex-col items-center gap-3">
                <div className="relative w-full max-w-[560px] select-none">
                    <div
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        className="cursor-grab border border-gray-300 bg-white p-4 active:cursor-grabbing"
                        style={{
                            transform: `translateX(${offset}px) rotate(${tilt}deg)`,
                            transition: dragStart.current === null ? 'transform 180ms ease-out' : 'none',
                            boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)',
                            touchAction: 'pan-y',
                        }}
                    >
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="font-['Jersey_10'] text-[30px] uppercase leading-none tracking-[1px] text-gray-900">
                                {candidate.name}
                            </span>
                            <Pill tone="yellow">Frank guessed {kindLabel(candidate.kindGuess).toLowerCase()}</Pill>
                        </div>

                        <p className="text-[12px] text-gray-500">
                            Seen {candidate.hits} times across {candidate.pageCount} pages
                        </p>

                        {candidate.sampleText && (
                            <p className="mt-2 border-l-2 border-gray-300 pl-2 text-[13px] italic text-gray-700">
                                &ldquo;{candidate.sampleText}&rdquo;
                            </p>
                        )}

                        <div className="mt-3 border-t border-gray-200 pt-2">
                            <p className="mb-1 font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                                Linked terms
                            </p>

                            <div className="flex flex-wrap items-center gap-1">
                                {terms.map(term => (
                                    <button
                                        key={term}
                                        type="button"
                                        onClick={() => setTerms(terms.filter(t => t !== term))}
                                        className="border border-gray-300 bg-offwhite px-2 py-[2px] text-[12px] text-gray-700 hover:bg-red-100"
                                        title="Remove"
                                    >
                                        {term} x
                                    </button>
                                ))}

                                <input
                                    type="text"
                                    value={termDraft}
                                    onChange={(e) => setTermDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); addTerm(); }
                                    }}
                                    placeholder="other spellings, then Enter"
                                    className="min-w-[160px] flex-1 border border-gray-300 px-2 py-[3px] text-[12px]"
                                />
                            </div>
                        </div>
                    </div>

                    {verdict && (
                        <span
                            className={`pointer-events-none absolute top-4 font-['Jersey_10'] text-[28px] uppercase tracking-[2px] ${
                                verdict === 'file' ? 'left-4 text-green-600' : 'right-4 text-red-600'
                            }`}
                        >
                            {verdict === 'file' ? 'File it' : 'Not a thing'}
                        </span>
                    )}
                </div>

                <div className="w-full max-w-[560px]">
                    <p className="mb-1 font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                        What is it? Tick everything it is.
                    </p>

                    <div className="flex flex-wrap gap-1">
                        {ENTITY_KINDS.map((option, position) => (
                            <button
                                key={option.value}
                                type="button"
                                title={option.hint}
                                onClick={() => toggleKind(option.value)}
                                className={`border px-2 py-1 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] transition-colors ${
                                    kinds.includes(option.value)
                                        ? 'border-black/30 text-white'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                                style={kinds.includes(option.value) ? { backgroundColor: ASKFRANK_TINT } : undefined}
                            >
                                <span className="mr-1 text-[11px] opacity-60">{position + 1}</span>
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {kinds.length > 0 && (
                    <div className="w-full max-w-[560px] border border-gray-200 bg-white p-2">
                        <p className="mb-1 font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                            Context cues
                        </p>
                        <p className="mb-2 text-[11px] text-gray-500">
                            {kinds.length > 1
                                ? 'This name means more than one thing, so tell Frank which words point at which. A question carrying one of these picks that meaning.'
                                : 'Words that mean this one, if the name could ever mean something else. Optional.'}
                        </p>

                        <div className="flex flex-col gap-2">
                            {kinds.map(kind => (
                                <label key={kind} className="flex flex-wrap items-center gap-2">
                                    <span className="w-[80px] shrink-0 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-700">
                                        {kindLabel(kind)}
                                    </span>
                                    <input
                                        type="text"
                                        value={cues[kind] || ''}
                                        onChange={(e) => setCues({ ...cues, [kind]: e.target.value })}
                                        placeholder={
                                            kind === 'furni' ? 'worth, value, rare'
                                                : kind === 'fansite' ? 'site, forum, .me'
                                                    : 'words that mean this one, comma separated'
                                        }
                                        className="min-w-[200px] flex-1 border border-gray-300 px-2 py-[4px] text-[12px]"
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex w-full max-w-[560px] flex-wrap items-center gap-2">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={dismiss}
                        className="bg-red-700 px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white disabled:opacity-50"
                    >
                        Not a thing
                    </button>

                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => setIndex(index + 1)}
                        className={TINY_BUTTON_CLASS}
                    >
                        Skip for now
                    </button>

                    <button
                        type="button"
                        disabled={busy}
                        onClick={file}
                        className="ml-auto bg-green-600 px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white disabled:opacity-50"
                    >
                        {fileLabel}
                    </button>
                </div>

                <button type="button" onClick={onClose} className={DESK_BUTTON_CLASS}>
                    Back to the list
                </button>
            </div>
        </DeskPanel>
    );
}

export default NameReview;
